import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ChatResponse,
  KpisByCategory,
  Message,
  ResponseState,
  Segment,
  Study,
  StudyMilestones
} from '@models';
import { environment } from 'environments/environment';
import { catchError, map, Observable, of } from 'rxjs';

import { UserDataService } from '../userdata/userdata.service';

@Injectable({
  providedIn: 'root'
})
export class StudyInsightsService {
  private readonly BASE_URL = environment.ENVIRONMENT_URL;
  private http: HttpClient = inject(HttpClient);
  private userDataService = inject(UserDataService);

  getStudyInsights(
    milestones: StudyMilestones,
    kpis: KpisByCategory,
    agent = 'studyinsights'
  ): Observable<ResponseState<Message[]>> {
    const content = this.encodeJSON(milestones, kpis);
    const metadata = this.createMetadata();
    const payload = this.createPayload(content, metadata);
    return this.http
      .post<ChatResponse>(`${this.BASE_URL}/api/crg-ai-service/api/v1/agents/${agent}`, payload, {
        withCredentials: true
      })
      .pipe(
        map((data) => {
          return this.mapMessagesResponse(data);
        }),
        catchError((error) => {
          return of({
            status: 'error',
            errorCode: error.status
          } as const);
        })
      );
  }

  getPortfolioSummary(
    studies: Study[],
    filters?: Record<string, any>
  ): Observable<ResponseState<Message[]>> {
    const content = this.encodeStudiesJSON(studies);
    const sanitizedFilters = this.transformSetsToArrays(filters ?? {});
    const metadata = this.createMetadata(sanitizedFilters);
    const payload = this.createPayload(content, metadata);
    return this.http
      .post<ChatResponse>(
        `${this.BASE_URL}/api/crg-ai-service/api/v1/agents/portfolioinsights`,
        payload,
        {
          withCredentials: true
        }
      )
      .pipe(
        map((data) => {
          return this.mapMessagesResponse(data);
        }),
        catchError((error) => {
          return of({
            status: 'error',
            errorCode: error.status
          } as const);
        })
      );
  }

  private mapMessagesResponse(data: ChatResponse) {
    const parsedMessages = data.messages.map((message) => {
      message.content = this.tryParseSegments(message.content);
      return message;
    });
    return {
      data: parsedMessages,
      status: 'success'
    } as const;
  }

  private tryParseSegments(value: any): Segment[] | string {
    if (typeof value !== 'string') return value as any;

    const first = value.trim()[0];
    if (first !== '{' && first !== '[') {
      return value; // definitely not JSON-like
    }

    try {
      const json = JSON.parse(value);
      return json.segments === undefined ? value : json.segments;
    } catch {
      // Not valid JSON, just return original string
      return value;
    }
  }

  private createPayload(content: string, metadata: Record<string, any>) {
    return {
      messages: [
        {
          role: 'user',
          content: content
        }
      ],
      metadata: metadata
    };
  }

  private encodeJSON(milestones: StudyMilestones, kpis: KpisByCategory) {
    return JSON.stringify({
      milestones: milestones,
      kpis: kpis
    });
  }

  private createMetadata(filters?: Record<string, any>): Record<string, any> {
    const clientNames = this.userDataService.getParentClientNames();
    const orgIds = this.userDataService.getUserOrgIds();
    const username = this.userDataService.getUsername();
    return {
      org_ids: orgIds,
      user_id: username,
      parent_client_names: clientNames,
      standard_client_names: clientNames,
      filters: filters
    };
  }

  private encodeStudiesJSON(studies: Study[]) {
    return JSON.stringify(studies);
  }

  private transformSetsToArrays<T extends Record<string, any>>(input: T): Record<any, any> {
    const result = {} as any;

    for (const key in input) {
      const value: any = input[key];

      if (value instanceof Set) {
        result[key] = Array.from(value);
      } else {
        result[key] = value;
      }
    }

    return result;
  }
}
